import React, { useState } from 'react';
import {
  Box,
  Button,
  Container,
  SvgIcon,
  Typography,
  useTheme,
  useMediaQuery,
  Tooltip
} from '@mui/material';
import {
  Logout,
  Map,
  ShoppingBasket
} from '@mui/icons-material';
import { getApiUrl } from '../config';
import {
  GlassBackground,
  GlassContainer,
  GlassIconButton,
  GlassShimmer,
  GlassSearchBox,
  useGlassShimmer,
  GlassInfoCard,
} from './ui/GlassComponents';
import {
  GlassPageHeader,
  GlassProfileGrid,
  GlassStatusText,
} from './ui/GlassLayoutComponents';
import CharacterAvatar from './CharacterAvatar';

const WikiIcon = (props) => (
  <SvgIcon {...props} viewBox="0 0 24 24">
    <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z" />
    <path d="M8,12H16V14H8V12M8,16H13V18H8V16Z" />
  </SvgIcon>
);

const CharacterSelector = ({ authData, onLogout, onSelectCharacter }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const shimmerRef = useGlassShimmer('.glass-card, .character-card');

  const handleCharacterClick = async (character) => {
    setLoading(true);
    setError('');

    // Handle Divine Entity (admin panel) selection
    if (character.isAdmin) {
      try {
        const response = await fetch(getApiUrl('/auth/select-character'), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            sessionToken: authData.sessionToken,
            displayName: "Divine Entity"
          })
        });

        const result = await response.json();

        if (response.ok && result.success) {
          // Pass the admin panel info to parent
          onSelectCharacter({
            token: result.token,
            uuid: authData.uuid,
            selectedDisplayName: "Divine Entity",
            character: result.character,
            isAdminPanel: true
          });
        } else {
          setError(result.message || 'Failed to access admin panel. Please try again.');
        }
      } catch (err) {
        console.error('Admin panel access error:', err);
        setError('Failed to connect to server. Please try again.');
      } finally {
        setLoading(false);
      }
      return;
    }

    // Handle regular character selection
    try {
      // Call the character selection API using session token
      const response = await fetch(getApiUrl('/auth/select-character'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          sessionToken: authData.sessionToken,
          displayName: character.name
        })
      });

      const result = await response.json();

      if (response.ok && result.success) {
        // Pass the JWT token and character info to the parent
        onSelectCharacter({
          token: result.token,
          uuid: authData.uuid,
          selectedDisplayName: result.selectedDisplayName,
          character: character
        });
      } else {
        setError(result.message || 'Failed to select character. Please try again.');
      }
    } catch (err) {
      console.error('Character selection error:', err);
      setError('Failed to connect to server. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Convert display names to profile objects for the generic components
  const profiles = authData.displayNames.map((displayName, index) => ({
    id: index + 1,
    name: displayName.name,
    variant: displayName.isDeceased ? 'deceased' : 'default',
    isDeceased: Boolean(displayName.isDeceased),
    avatarComponent: (
      <CharacterAvatar
        key={displayName.profilePictureUrl || `no-picture-${index}`}
        profilePictureUrl={displayName.profilePictureUrl}
        characterName={displayName.name}
        size={80}
        isRecent={displayName.name === authData.mostRecentDisplayName}
        isDeceased={Boolean(displayName.isDeceased)}
        sx={{
          mx: 'auto',
          mb: 2,
          border: '2px solid rgba(255, 255, 255, 0.1)',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)',
        }}
      />
    ),
    badges: Boolean(displayName.isDeceased) ? [{
      variant: 'deceased',
      label: 'Deceased',
      icon: <span style={{ fontSize: 14 }}>💀</span>
    }] : [],
    statusBadges: displayName.name === authData.mostRecentDisplayName && !displayName.isDeceased ? [{
      variant: 'success',
      label: 'Most Recent'
    }] : [],
    timestamps: {
      lastSeen: displayName.lastSeen,
      firstUsed: displayName.firstUsed
    },
    // Keep original data for onClick handler
    originalData: displayName
  }));

  // Create Divine Entity pseudo-character for admins
  const divineEntity = authData.isAdmin ? {
    id: 0,
    name: "Divine Entity",
    variant: 'admin',
    isDeceased: false,
    isAdmin: true,
    avatarComponent: (
      <CharacterAvatar
        key="divine-entity-avatar"
        profilePictureUrl={null}
        characterName="Divine Entity"
        size={80}
        isRecent={false}
        isDeceased={false}
        sx={{
          mx: 'auto',
          mb: 2,
          border: '2px solid rgba(255, 255, 255, 0.1)',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)',
        }}
      />
    ),
    badges: [{
      variant: 'admin',
      label: 'Admin Panel'
    }],
    statusBadges: [],
    timestamps: null, // No timestamps for Divine Entity
    originalData: {
      name: "Divine Entity",
      isAdmin: true,
      lastSeen: Date.now(),
      firstUsed: Date.now(),
      profilePictureUrl: null,
      isRecent: false
    }
  } : null;

  // Filter profiles based on search query
  const filteredProfiles = profiles.filter(profile =>
    profile.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Separate alive and deceased characters
  const aliveCharacters = filteredProfiles.filter(profile => !profile.isDeceased);
  const deceasedCharacters = filteredProfiles.filter(profile => profile.isDeceased);

  // Build the final profile list: Divine Entity first, then alive, then deceased
  const allProfiles = [];
  if (divineEntity && divineEntity.name.toLowerCase().includes(searchQuery.toLowerCase())) {
    allProfiles.push(divineEntity);
  }
  allProfiles.push(...aliveCharacters, ...deceasedCharacters);

  return (
    <GlassBackground>
      {/* Shimmer shadow element */}
      <GlassShimmer ref={shimmerRef} />

      <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 2 }}>
        {/* Logo and Quick Links Container */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            alignItems: 'center',
            gap: isMobile ? 2 : 4,
            mb: 3,
            justifyContent: 'center',
          }}
        >
          {/* Statecraft Logo */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <img
              src="/statecraft.png"
              alt="Statecraft"
              style={{
                maxWidth: isMobile ? '200px' : '180px',
                height: 'auto',
                filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.3))',
              }}
            />
          </Box>

          {/* Quick Links */}
          <Box
            sx={{
              display: 'flex',
              gap: isMobile ? 1.5 : 3,
              justifyContent: 'center',
              flexWrap: 'wrap',
            }}
          >
            {/* Store Button */}
            <Box
              className="shimmer-button"
              sx={{
                position: 'relative',
                overflow: 'hidden',
                borderRadius: '8px',
                border: '1px solid rgba(255, 255, 255, 0.15)',
                background: 'rgba(255, 255, 255, 0.08)',
                backdropFilter: 'blur(20px)',
                boxShadow: 'inset 0 1px 0 rgba(255, 255, 255, 0.1), 0 4px 12px rgba(0, 0, 0, 0.15)',
              }}
            >
              <Button
                onClick={() => window.open('https://store.landfall.world', '_blank')}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: isMobile ? 0 : 1.5,
                  padding: isMobile ? '12px' : '32px 48px',
                  color: 'white',
                  textTransform: 'none',
                  fontSize: isMobile ? '0.9rem' : '1.2rem',
                  fontWeight: 500,
                  width: isMobile ? '60px' : '180px',
                  height: isMobile ? '60px' : '120px',
                  minWidth: isMobile ? '60px' : '180px',
                  minHeight: isMobile ? '60px' : '120px',
                  borderRadius: '8px',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.05)',
                  },
                }}
              >
                <ShoppingBasket sx={{ fontSize: isMobile ? '2rem' : '2.5rem' }} />
                {!isMobile && 'Store'}
              </Button>
            </Box>

            {/* Map Button */}
            <Box
              className="shimmer-button"
              sx={{
                position: 'relative',
                overflow: 'hidden',
                borderRadius: '8px',
                border: '1px solid rgba(255, 255, 255, 0.15)',
                background: 'rgba(255, 255, 255, 0.08)',
                backdropFilter: 'blur(20px)',
                boxShadow: 'inset 0 1px 0 rgba(255, 255, 255, 0.1), 0 4px 12px rgba(0, 0, 0, 0.15)',
              }}
            >
              <Button
                onClick={() => window.open('https://map.landfall.world', '_blank')}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: isMobile ? 0 : 1.5,
                  padding: isMobile ? '12px' : '32px 48px',
                  color: 'white',
                  textTransform: 'none',
                  fontSize: isMobile ? '0.9rem' : '1.2rem',
                  fontWeight: 500,
                  width: isMobile ? '60px' : '180px',
                  height: isMobile ? '60px' : '120px',
                  minWidth: isMobile ? '60px' : '180px',
                  minHeight: isMobile ? '60px' : '120px',
                  borderRadius: '8px',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.05)',
                  },
                }}
              >
                <Map sx={{ fontSize: isMobile ? '2rem' : '2.5rem' }} />
                {!isMobile && 'Map'}
              </Button>
            </Box>

            {/* Wiki Button */}
            <Box
              className="shimmer-button"
              sx={{
                position: 'relative',
                overflow: 'hidden',
                borderRadius: '8px',
                border: '1px solid rgba(255, 255, 255, 0.15)',
                background: 'rgba(255, 255, 255, 0.08)',
                backdropFilter: 'blur(20px)',
                boxShadow: 'inset 0 1px 0 rgba(255, 255, 255, 0.1), 0 4px 12px rgba(0, 0, 0, 0.15)',
              }}
            >
              <Button
                onClick={() => window.open('https://wiki.landfall.world', '_blank')}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: isMobile ? 0 : 1.5,
                  padding: isMobile ? '12px' : '32px 48px',
                  color: 'white',
                  textTransform: 'none',
                  fontSize: isMobile ? '0.9rem' : '1.2rem',
                  fontWeight: 500,
                  width: isMobile ? '60px' : '180px',
                  height: isMobile ? '60px' : '120px',
                  minWidth: isMobile ? '60px' : '180px',
                  minHeight: isMobile ? '60px' : '120px',
                  borderRadius: '8px',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.05)',
                  },
                }}
              >
                <WikiIcon sx={{ fontSize: isMobile ? '2rem' : '2.5rem' }} />
                {!isMobile && 'Wiki'}
              </Button>
            </Box>
          </Box>
        </Box>

        {/* Main glass container */}
        <GlassContainer className="glass-card">
          {/* Header with logout button */}
          <GlassPageHeader
            title="Select a Character"
            subtitle={`Choose from your ${authData.displayNameCount} characters to continue`}
            action={
              <>
                {!isMobile && (
                  <Typography
                    variant="body2"
                    sx={{
                      color: 'rgba(255, 255, 255, 0.6)',
                      mr: 1
                    }}
                  >
                    {authData.uuid.substring(0, 8)}...
                  </Typography>
                )}
                <Tooltip title="Logout" placement="left">
                  <GlassIconButton onClick={onLogout}>
                    <Logout />
                  </GlassIconButton>
                </Tooltip>
              </>
            }
          />

          {authData.hasNoCharacters && (
            <GlassInfoCard severity="info">
              No characters found. Your display names will be tracked when you join the server.
              {authData.isAdmin && " As an admin, you can still access the admin panel using Divine Entity."}
            </GlassInfoCard>
          )}

          {error && (
            <GlassInfoCard severity="error">
              {error}
            </GlassInfoCard>
          )}

          {/* Search Box */}
          <GlassSearchBox
            placeholder="Search characters..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onClear={() => setSearchQuery('')}
          />

          {/* Character Grid */}
          <GlassProfileGrid
            profiles={allProfiles}
            loading={loading}
            onProfileClick={(profile) => handleCharacterClick(profile.originalData)}
          />

          {/* No Results Message */}
          {allProfiles.length === 0 && searchQuery && (
            <GlassStatusText variant="empty">
              No characters found matching "{searchQuery}"
            </GlassStatusText>
          )}

          {loading && (
            <GlassStatusText variant="loading">
              Loading character...
            </GlassStatusText>
          )}
        </GlassContainer>
      </Container>
    </GlassBackground>
  );
};

export default CharacterSelector; 